import { useState } from 'react';
import { useTarefas } from '@/api/queries/tarefas';
import { useTarefaAction, type TarefaAction } from '@/api/mutations/tarefa';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Plus, Check, Clock, AlertTriangle, Pencil, ChevronDown } from 'lucide-react';
import { TarefaForm } from '@/components/TarefaForm';
import { cn } from '@/lib/cn';
import { toast } from 'sonner';
import type { Tarefa } from '@/api/types';

function formatVencimento(dataVenc: string) {
  return new Date(dataVenc).toLocaleString('pt-BR', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function TabTarefas({ empresaId }: { empresaId: string }) {
  const { data, isLoading } = useTarefas({ empresa_id: empresaId, limit: 100 });
  const action = useTarefaAction();
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Tarefa | null>(null);
  const [menuFor, setMenuFor] = useState<string | null>(null);

  async function exec(id: string, a: TarefaAction) {
    setMenuFor(null);
    try {
      await action.mutateAsync({ id, action: a });
      toast.success('OK');
    } catch (e) {
      toast.error('Falha: ' + (e instanceof Error ? e.message : 'erro'));
    }
  }

  function openNew() {
    setEditing(null);
    setFormOpen(true);
  }

  if (isLoading) return <Skeleton className="h-32 mt-4" />;

  return (
    <div className="mt-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-muted-foreground">
          {data?.length ?? 0} tarefa{(data?.length ?? 0) === 1 ? '' : 's'}
        </h3>
        <button
          type="button"
          onClick={openNew}
          className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" />
          Nova tarefa
        </button>
      </div>

      {!data || data.length === 0 ? (
        <p className="text-muted-foreground">Sem tarefas.</p>
      ) : (
        <div className="space-y-2">
          {data.map((t) => {
            const concluida = t.status === 'concluida';
            const cancelada = t.status === 'cancelada';
            return (
              <Card
                key={t.id}
                className={cn(
                  'p-3 flex items-start gap-3 relative group',
                  t.sla_estourou && 'border-l-4 border-l-destructive',
                  concluida && 'opacity-50',
                )}
              >
                <button
                  type="button"
                  onClick={() => exec(t.id, concluida ? 'reabrir' : 'concluir')}
                  className={cn(
                    'inline-flex h-6 w-6 items-center justify-center rounded border-2 mt-0.5 shrink-0',
                    concluida
                      ? 'bg-primary border-primary text-primary-foreground'
                      : 'border-muted-foreground hover:border-primary',
                  )}
                >
                  {concluida && <Check className="h-3.5 w-3.5" />}
                </button>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs uppercase font-medium text-muted-foreground">
                      {t.tipo.replace(/_/g, ' ')}
                    </span>
                    {t.prioridade === 'alta' && (
                      <Badge variant="destructive" className="text-xs px-1.5 py-0">
                        alta
                      </Badge>
                    )}
                  </div>
                  {t.descricao_livre && (
                    <p className={cn('text-sm', concluida && 'line-through')}>
                      {t.descricao_livre}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground inline-flex items-center gap-1 mt-1">
                    {t.sla_estourou ? (
                      <>
                        <AlertTriangle className="h-3 w-3 text-destructive" />
                        SLA estourado
                      </>
                    ) : (
                      <>
                        <Clock className="h-3 w-3" />
                        {formatVencimento(t.data_vencimento)}
                      </>
                    )}
                    {' · '}
                    energia {t.energia_requerida}
                  </p>
                </div>

                <div className="flex items-center gap-1 shrink-0">
                  <button
                    type="button"
                    onClick={() => {
                      setEditing(t);
                      setFormOpen(true);
                    }}
                    className="inline-flex h-7 w-7 items-center justify-center rounded text-muted-foreground opacity-0 group-hover:opacity-100 hover:bg-muted"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  {!concluida && !cancelada && (
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setMenuFor(menuFor === t.id ? null : t.id)}
                        className="inline-flex items-center gap-0.5 rounded-md border px-2 py-1 text-xs hover:bg-muted"
                      >
                        Adiar
                        <ChevronDown className="h-3 w-3" />
                      </button>
                      {menuFor === t.id && (
                        <div className="absolute right-0 top-full z-20 mt-1 min-w-32 rounded-md border bg-popover p-1 shadow-md">
                          <button
                            type="button"
                            onClick={() => exec(t.id, 'adiar_1h')}
                            className="block w-full rounded-sm px-2 py-1 text-left text-xs hover:bg-accent"
                          >
                            +1 hora
                          </button>
                          <button
                            type="button"
                            onClick={() => exec(t.id, 'adiar_1d')}
                            className="block w-full rounded-sm px-2 py-1 text-left text-xs hover:bg-accent"
                          >
                            +1 dia
                          </button>
                          <button
                            type="button"
                            onClick={() => exec(t.id, 'adiar_7d')}
                            className="block w-full rounded-sm px-2 py-1 text-left text-xs hover:bg-accent"
                          >
                            +1 semana
                          </button>
                          <button
                            type="button"
                            onClick={() => exec(t.id, 'cancelar')}
                            className="block w-full rounded-sm px-2 py-1 text-left text-xs text-destructive hover:bg-destructive/10"
                          >
                            Cancelar
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <TarefaForm
        empresaId={empresaId}
        tarefa={editing}
        open={formOpen}
        onOpenChange={setFormOpen}
      />
    </div>
  );
}
